/**
 * Stage L cosmetics, client side: what every player of the current game wears.
 *
 * Fed once per game: multiplayer from `gameStart.cosmetics` (server-checked loadouts), singleplayer
 * from the local player's own loadout. Renderers read resolved params from here; the map shader
 * reads them through the palette rows this module writes. Visual / audio only: nothing here ever
 * reaches the simulation.
 */
import type { PlayerID } from "../../../core/types";
import {
  BORDER_PATTERNS,
  FILL_TEXTURES,
  acceptCustomColors,
  catalogIndex,
  hexToRgb,
  isEmptyResolved,
  resolveLoadout,
  sanitizeLoadout,
  type CatalogIndex,
  type Loadout,
  type ResolvedLoadout,
  type Rgb8,
} from "../../../shared/cosmetics";
import type { PaletteData } from "../palette";
import { PALETTE_HEIGHT, PALETTE_WIDTH } from "../palette";
import { COSM_ACCENT_ROW, COSM_BORDER_ROW, COSM_CODE_ROW, COSM_MAX_ID } from "./paletteRows";

export class CosmeticsRegistry {
  private readonly worn = new Map<PlayerID, ResolvedLoadout>();
  /** Public client id per player (multiplayer; reports from the game-over screen). */
  private readonly clients = new Map<PlayerID, string>();
  /** Players whose custom territory colour passed the distinctness rule. */
  private accepted = new Set<PlayerID>();
  private index: CatalogIndex = catalogIndex();
  /** Bumped on every change (renderers re-read when it moves). */
  version = 0;
  /** Master switch (settings: "show other players' cosmetics"). Own cosmetics always show. */
  showOthers = true;
  me: PlayerID | null = null;

  setCatalog(index: CatalogIndex): void {
    this.index = index;
  }

  /** Replace everything (game start). Loadouts are sanitised against the catalog (no ownership: the server did that). */
  load(
    loadouts: Record<number, Loadout> | ReadonlyMap<PlayerID, Loadout> | undefined,
    me: PlayerID | null,
    players?: readonly { playerID: PlayerID; clientID: string }[],
  ): void {
    this.worn.clear();
    this.clients.clear();
    for (const p of players ?? []) this.clients.set(p.playerID, p.clientID);
    this.accepted.clear();
    this.me = me;
    const entries: [number, Loadout][] = loadouts instanceof Map ? [...loadouts.entries()] : Object.entries(loadouts ?? {}).map(([k, v]) => [Number(k), v as Loadout]);
    for (const [id, l] of entries) {
      if (!Number.isInteger(id) || id <= 0) continue;
      const r = resolveLoadout(sanitizeLoadout(l, this.index), this.index);
      if (!isEmptyResolved(r)) this.worn.set(id, r);
    }
    this.version++;
  }

  clear(): void {
    this.load(undefined, null);
  }

  /** Resolved params a player wears (undefined = nothing, or hidden by the settings). */
  get(id: PlayerID): ResolvedLoadout | undefined {
    if (!this.showOthers && id !== this.me) return undefined;
    return this.worn.get(id);
  }

  /** Public client id of a player (multiplayer only). */
  clientOf(id: PlayerID): string | undefined {
    return this.clients.get(id);
  }

  /** Every player wearing something (ids). */
  players(): PlayerID[] {
    return [...this.worn.keys()];
  }

  get size(): number {
    return this.worn.size;
  }

  /**
   * Decide which custom territory colours are used (the same on every client): call when the
   * base colours are known (game start / palette options change). `baseOf` gives the assigned
   * colour; `ids` are the players to keep apart (humans and nations).
   */
  settleColors(ids: readonly PlayerID[], baseOf: (id: PlayerID) => Rgb8): void {
    const base = new Map<number, Rgb8>();
    for (const id of ids) base.set(id, baseOf(id));
    const wanted = new Map<number, Rgb8>();
    for (const [id, r] of this.worn) if (r.colorSet) wanted.set(id, hexToRgb(r.colorSet.territory));
    this.accepted = acceptCustomColors(base, wanted);
    this.version++;
  }

  /** The custom territory colour a player shows, or undefined (assigned colour). */
  territoryColor(id: PlayerID): Rgb8 | undefined {
    const r = this.get(id);
    if (!r?.colorSet || !this.accepted.has(id)) return undefined;
    return hexToRgb(r.colorSet.territory);
  }

  /** The custom border colour a player shows (only with an accepted colour set). */
  borderColor(id: PlayerID): Rgb8 | undefined {
    const r = this.get(id);
    if (!r?.colorSet || !this.accepted.has(id)) return undefined;
    return hexToRgb(r.colorSet.border);
  }

  private written = -1;
  private writtenOthers = true;
  private readonly writtenIds = new Set<PlayerID>();

  /**
   * Write the map cosmetics (border pattern, fill texture, colour-set border) into the palette's
   * upper rows. Cheap when nothing changed. `colorsOn` false (colour-blind / team palettes) skips
   * the colour-set border so readability palettes always win.
   */
  writePalette(pal: PaletteData, colorsOn: boolean): void {
    const key = this.version * 2 + (colorsOn ? 1 : 0);
    if (key === this.written && this.writtenOthers === this.showOthers) return;
    this.written = key;
    this.writtenOthers = this.showOthers;
    const d = pal.data;
    let r0 = PALETTE_HEIGHT;
    let r1 = 0;
    const mark = (row: number) => {
      if (row < r0) r0 = row;
      if (row + 1 > r1) r1 = row + 1;
    };
    // Clear the entries written last time.
    for (const id of this.writtenIds) {
      for (const base of [COSM_CODE_ROW, COSM_ACCENT_ROW, COSM_BORDER_ROW]) {
        const row = base + (id >> 8);
        const o = (row * PALETTE_WIDTH + (id & 255)) * 4;
        d[o] = d[o + 1] = d[o + 2] = d[o + 3] = 0;
        mark(row);
      }
    }
    this.writtenIds.clear();
    for (const id of this.worn.keys()) {
      const r = this.get(id);
      if (!r || id > COSM_MAX_ID) continue;
      const col = id & 255;
      const hi = id >> 8;
      const code = ((COSM_CODE_ROW + hi) * PALETTE_WIDTH + col) * 4;
      d[code] = r.border ? BORDER_PATTERNS.indexOf(r.border.pattern) + 1 : 0;
      d[code + 1] = r.fill ? (FILL_TEXTURES.indexOf(r.fill.texture) + 1) | ((r.fill.strength - 1) << 4) : 0;
      d[code + 2] = 0;
      d[code + 3] = 255;
      mark(COSM_CODE_ROW + hi);
      if (r.border) {
        const a = hexToRgb(r.border.accent);
        const o = ((COSM_ACCENT_ROW + hi) * PALETTE_WIDTH + col) * 4;
        d[o] = a.r;
        d[o + 1] = a.g;
        d[o + 2] = a.b;
        d[o + 3] = 255;
        mark(COSM_ACCENT_ROW + hi);
      }
      const b = colorsOn ? this.borderColor(id) : undefined;
      if (b) {
        const o = ((COSM_BORDER_ROW + hi) * PALETTE_WIDTH + col) * 4;
        d[o] = b.r;
        d[o + 1] = b.g;
        d[o + 2] = b.b;
        d[o + 3] = 255;
        mark(COSM_BORDER_ROW + hi);
      }
      this.writtenIds.add(id);
    }
    if (r1 > r0) {
      pal.dirtyRow0 = Math.min(pal.dirtyRow0, r0);
      pal.dirtyRow1 = Math.max(pal.dirtyRow1, r1);
      pal.version++;
    }
  }
}

/** The registry of the running game (one game at a time in the client). */
export const gameCosmetics = new CosmeticsRegistry();
